import React from 'react';
import { Navigate, useNavigate } from 'react-router';
import { useApptekster } from '../../contexts/ApptekstContext';
import { Paragraph } from '../../components/Paragraph';
import { useSkiltbestillingState } from './Skiltbestilling';
import { Skiltvisning } from './internals/Skiltvisning';
import { SVVBreadcrumbLink } from '@komponentkassen/svv-breadcrumbs';
import { SVVLayoutBanner } from '@komponentkassen/svv-layout';
import { SVVInformationMessage } from '@komponentkassen/svv-information-message';
import { SVVTextLink } from '@komponentkassen/svv-link';
import { SVVButton } from '@komponentkassen/svv-button';

export const BestillingBekreftelse: React.FC = () => {
    const t = useApptekster();
    const navigate = useNavigate();

    const bestillingDato = useSkiltbestillingState(
        (state) => state.bestillingDato,
    );
    const kjennemerkestorrelser = useSkiltbestillingState(
        (state) => state.kjennemerkestorrelser,
    );
    const kjennemerketype = useSkiltbestillingState(
        (state) => state.kjennemerketype,
    );
    const tegnkombinasjon = useSkiltbestillingState(
        (state) => state.tegnkombinasjon,
    );
    const trafikkstasjon = useSkiltbestillingState(
        (state) => state.trafikkstasjon,
    );

    if (!bestillingDato) {
        return <Navigate to="/" />;
    }

    const kjennemerkestorrelserListe = [
        kjennemerkestorrelser.foran,
        kjennemerkestorrelser.bak,
    ].filter((km) => km);

    return (
        <>
            <SVVLayoutBanner>
                <SVVBreadcrumbLink
                    href="#/statusoversikt"
                    ariaLabel={t('personlig-kjennemerke.snarvei.status')}
                    className="intern-link"
                >
                    {t('personlig-kjennemerke.snarvei.status')}
                </SVVBreadcrumbLink>
                <h1 className="svv-h1--spacing">
                    {t('personlig-kjennemerke.bestill.bekreftelse.overskrift')}
                </h1>
                <SVVInformationMessage
                    type="success"
                    className="spacing-bottom--medium"
                >
                    {t(
                        'personlig-kjennemerke.bestill.bekreftelse.personlig-skilt-bestilt',
                        tegnkombinasjon,
                    )}
                </SVVInformationMessage>
                <h2 className="svv-h2--spacing">
                    {t(
                        'personlig-kjennemerke.bestill.bekreftelse.hva-skjer-videre',
                    )}
                </h2>
                <Paragraph className="spacing-bottom--medium">
                    {t(
                        'personlig-kjennemerke.bestill.bekreftelse.hva-skjer-videre.info',
                    )}{' '}
                    <SVVTextLink
                        url="https://www.vegvesen.no/dinside/profil"
                        text={t(
                            'personlig-kjennemerke.bestill.bekreftelse.hva-skjer-videre.les-mer',
                        )}
                    />
                </Paragraph>
                <SVVButton onClick={() => navigate('/statusoversikt')}>
                    {t('personlig-kjennemerke.delt.til-statusoversikten')}
                </SVVButton>
            </SVVLayoutBanner>
            <SVVLayoutBanner secondary>
                <h3 className="svv-h2 svv-h3--spacing">
                    {t(
                        'personlig-kjennemerke.bestill.bekreftelse.din-bestilling',
                    )}
                </h3>
                <div className="spacing-bottom--medium" data-hj-suppress={true}>
                    <p>
                        {t('personlig-kjennemerke.bestill.oppsummering.dato')}:{' '}
                        {bestillingDato}
                    </p>
                    <p>
                        {t(
                            'personlig-kjennemerke.bestill.oppsummering.personlig-skilt',
                        )}
                        : {tegnkombinasjon}
                    </p>
                    <p>
                        {t(
                            'personlig-kjennemerke.bestill.oppsummering.antall-skilt',
                        )}
                        : {kjennemerkestorrelserListe.length}
                    </p>
                    <p>
                        {t(
                            'personlig-kjennemerke.bestill.oppsummering.trafikkstasjon',
                        )}
                        : {trafikkstasjon?.navn}
                    </p>
                </div>
                <Skiltvisning
                    modus="detaljer"
                    className="spacing-bottom--medium"
                    kjennemerkestorrelser={kjennemerkestorrelser}
                    kjennemerketype={kjennemerketype}
                    tegnkombinasjon={tegnkombinasjon}
                />
            </SVVLayoutBanner>
        </>
    );
};
